import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, IconButton, Badge, Tooltip, useToast } from "@chakra-ui/react";
import { FiBell } from "react-icons/fi";
import { useCognito } from "../utils/useCognito";

// ===== Config =====
const API_BASE = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod";
const POLL_MS = 30_000;

// Event names for cross-talk with NotificationsPage
const COUNTS_EVENT = "cg:notifications:updateCounts";
const COUNTS_END_EVENT = "cg:notifications:updateCounts:off";

async function apiGet(path, token) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: token ? `Bearer ${token}` : "" },
  });
  if (!res.ok) throw new Error(`GET ${path} -> ${res.status}`);
  return res.json();
}

const hoverScale1 = { transform: "scale(1)" };

const NotificationBell = ({
  onClick,
  iconSize = "md",
  ariaLabel = "Notifications",
}) => {
  const { getIdToken } = useCognito();
  const toast = useToast();

  const [token, setToken] = useState("");
  const [count, setCount] = useState(0);
  const [usingExternalCounts, setUsingExternalCounts] = useState(false);

  const pollRef = useRef(null);

  const clearPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const fetchCounts = useCallback(async () => {
    if (!token || usingExternalCounts) return;

    const qs = (bucket) =>
      `/notifications?bucket=${bucket}&status=open&limit=50`;

    const doFetch = async (authToken) => {
      const [a, c, u] = await Promise.all([
        apiGet(qs("action_required"), authToken),
        apiGet(qs("confirm"), authToken),
        apiGet(qs("update"), authToken),
      ]);
      const total =
        (a.items?.length || 0) +
        (c.items?.length || 0) +
        (u.items?.length || 0);
      setCount(total);
    };

    try {
      await doFetch(token);
    } catch (err) {
      const msg = String(err?.message || err);
      const is403 = msg.includes("403");

      if (is403) {
        // Token probably stale – grab a fresh *ID token* and retry once
        try {
          const fresh = await getIdToken();
          if (!fresh) return;
          setToken(fresh);
          await doFetch(fresh);
          return;
        } catch (e2) {
          console.warn("NotificationBell token refresh failed:", e2);
        }
      }

      console.warn("NotificationBell fetchCounts error:", msg);
      if (!is403) {
        toast({
          title: "Notifications unavailable",
          description: "Unable to refresh notification count.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      }
    }
  }, [token, usingExternalCounts, getIdToken, toast]);

  // Initial token fetch – SAME token kind as before (idToken)
  useEffect(() => {
    (async () => {
      try {
        const t = await getIdToken();
        setToken(t || "");
      } catch {
        // silent; bell just won't show a badge
      }
    })();
  }, [getIdToken]);

  // Start polling when we have a token and we're not using external counts
  useEffect(() => {
    if (!token || usingExternalCounts) return;

    // fire once immediately
    fetchCounts();

    // then poll
    clearPolling();
    pollRef.current = setInterval(() => {
      fetchCounts();
    }, POLL_MS);

    return () => clearPolling();
  }, [token, usingExternalCounts, fetchCounts]);

  // Listen for external counts from NotificationsPage
  useEffect(() => {
    const handleCounts = (e) => {
      const detail = e?.detail || {};
      if (typeof detail.total === "number") {
        setUsingExternalCounts(true);
        clearPolling();
        setCount(detail.total);
      }
    };

    const handleCountsEnd = () => {
      // NotificationsPage unmounted – go back to self-polling
      setUsingExternalCounts(false);
    };

    window.addEventListener(COUNTS_EVENT, handleCounts);
    window.addEventListener(COUNTS_END_EVENT, handleCountsEnd);
    return () => {
      window.removeEventListener(COUNTS_EVENT, handleCounts);
      window.removeEventListener(COUNTS_END_EVENT, handleCountsEnd);
    };
  }, []);

  return (
    <Tooltip label="Notifications" placement="right">
      <Box position="relative" display="inline-block">
        <IconButton
          aria-label={ariaLabel}
          icon={<FiBell />}
          variant="ghost"
          colorScheme="whiteAlpha"
          size={iconSize}
          onClick={onClick}
          _hover={hoverScale1}
        />
        {count > 0 && (
          <Badge
            position="absolute"
            top="-2px"
            right="-2px"
            borderRadius="full"
            px={2}
            fontSize="0.7em"
            colorScheme="red"
          >
            {count}
          </Badge>
        )}
      </Box>
    </Tooltip>
  );
};

export default NotificationBell;
